'use client'

import { useEffect, useRef } from 'react'

import {
  type LoginAfterLoggedIn,
  type LoginMethod,
} from '../loginAfterLoggedIn'
import { type LoginOrigin, loginOriginIsCart } from '../loginOrigin'
import { trackLoginPageView, trackLoginSuccess } from '../trackLogin'

type AfterLoggedIn = (
  afterLoggedInSideEffectsWithPreferredLanguage: Promise<string | undefined>,
) => void

type TrackingFlag = { current: boolean }

const loginTrackingTrackCartPageView = (
  origin: LoginOrigin,
  hasTrackedPageView: TrackingFlag,
): void => {
  if (!loginOriginIsCart(origin) || hasTrackedPageView.current) {
    return
  }

  hasTrackedPageView.current = true
  trackLoginPageView(origin)
}

const loginTrackingHandleSuccess = ({
  afterLoggedIn,
  afterLoggedInSideEffectsWithPreferredLanguage,
  hasTrackedLoginSuccess,
  method,
  origin,
}: {
  afterLoggedIn: AfterLoggedIn
  afterLoggedInSideEffectsWithPreferredLanguage: Promise<string | undefined>
  hasTrackedLoginSuccess: TrackingFlag
  method: LoginMethod
  origin: LoginOrigin
}): void => {
  if (!hasTrackedLoginSuccess.current) {
    hasTrackedLoginSuccess.current = true
    trackLoginSuccess(origin, method)
  }

  afterLoggedIn(afterLoggedInSideEffectsWithPreferredLanguage)
}

export const useLoginTracking = ({
  afterLoggedIn,
  origin,
}: {
  afterLoggedIn: AfterLoggedIn
  origin: LoginOrigin
}): LoginAfterLoggedIn => {
  const hasTrackedPageView = useRef(false)
  const hasTrackedLoginSuccess = useRef(false)

  useEffect(() => {
    loginTrackingTrackCartPageView(origin, hasTrackedPageView)
  }, [origin])

  return (
    afterLoggedInSideEffectsWithPreferredLanguage: Promise<string | undefined>,
    method: LoginMethod,
  ): void => {
    loginTrackingHandleSuccess({
      afterLoggedIn,
      afterLoggedInSideEffectsWithPreferredLanguage,
      hasTrackedLoginSuccess,
      method,
      origin,
    })
  }
}
